import { useCallback, useEffect, useRef, useState } from "react";
import { getSurveyStatistics } from "../api/survey.api";
import type { GetSurveyStatisticsRequest, SurveyStatisticsResponse } from "../types/survey.types";

interface UseSurveyStatisticsState {
  data: SurveyStatisticsResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

function createSurveyStatisticsRequest(): GetSurveyStatisticsRequest {
  const now = new Date();
  const dayOfWeek = now.getDay() || 7;
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - dayOfWeek + 1);
  startOfWeek.setHours(0, 0, 0, 0);

  const startOfNextWeek = new Date(startOfWeek);
  startOfNextWeek.setDate(startOfWeek.getDate() + 7);

  return { from: startOfWeek.toISOString(), to: startOfNextWeek.toISOString() };
}

export function useSurveyStatistics(): UseSurveyStatisticsState {
  const [data, setData] = useState<SurveyStatisticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latestRequestId = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++latestRequestId.current;
    try {
      setLoading(true);
      setError(null);
      const result = await getSurveyStatistics(createSurveyStatisticsRequest());
      if (requestId === latestRequestId.current) setData(result);
    } catch (error: unknown) {
      if (requestId === latestRequestId.current) {
        setError(error instanceof Error ? error.message : "Failed to load survey statistics");
        setData(null);
      }
    } finally {
      if (requestId === latestRequestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refresh: fetchData,
  };
}
